import { supabaseV2 } from "./supabase";

const SW_PATH = "/sw.js";
const VAPID_PUBLIC_KEY = (import.meta.env.VITE_WEB_PUSH_VAPID_PUBLIC_KEY as string | undefined) || "";

export type PushStatus = "unsupported" | "denied" | "granted" | "default" | "no-key";

export function isPushSupported(): boolean {
  return typeof window !== "undefined"
    && "serviceWorker" in navigator
    && "PushManager" in window
    && "Notification" in window;
}

export function getPermission(): NotificationPermission | "unsupported" {
  if (!isPushSupported()) return "unsupported";
  return Notification.permission;
}

export async function ensureServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!isPushSupported()) return null;
  const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (existing) return existing;
  return navigator.serviceWorker.register(SW_PATH, { scope: "/" });
}

export async function getCurrentSubscription(): Promise<PushSubscription | null> {
  const reg = await ensureServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  // Allochiamo esplicitamente un ArrayBuffer per soddisfare BufferSource:
  // i lib.dom recenti rifiutano Uint8Array<ArrayBufferLike> per via di
  // SharedArrayBuffer (TS2322).
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

function arrayBufferToBase64Url(buffer: ArrayBuffer | null): string {
  if (!buffer) return "";
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.length; i += 1) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

async function persistSubscription(sub: PushSubscription) {
  const json = sub.toJSON();
  const endpoint = json.endpoint || sub.endpoint;
  const p256dh = (json.keys && json.keys.p256dh) || arrayBufferToBase64Url(sub.getKey?.("p256dh") ?? null);
  const authSecret = (json.keys && json.keys.auth) || arrayBufferToBase64Url(sub.getKey?.("auth") ?? null);
  if (!endpoint || !p256dh || !authSecret) {
    throw new Error("Subscription incompleta");
  }
  const { error } = await supabaseV2.rpc("upsert_push_subscription", {
    _endpoint: endpoint,
    _p256dh: p256dh,
    _auth: authSecret,
    _user_agent: navigator.userAgent,
  });
  if (error) throw error;
}

export async function subscribeToPush(): Promise<{ status: PushStatus; subscription: PushSubscription | null; }> {
  if (!isPushSupported()) return { status: "unsupported", subscription: null };
  if (!VAPID_PUBLIC_KEY) return { status: "no-key", subscription: null };

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { status: permission as PushStatus, subscription: null };
  }
  const reg = await ensureServiceWorker();
  if (!reg) return { status: "unsupported", subscription: null };
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await persistSubscription(existing);
    return { status: "granted", subscription: existing };
  }
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
  });
  await persistSubscription(sub);
  return { status: "granted", subscription: sub };
}

export async function unsubscribeFromPush(): Promise<void> {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  await sub.unsubscribe();
  await supabaseV2.rpc("revoke_push_subscription", { _endpoint: endpoint });
}

export async function refreshSubscriptionIfNeeded() {
  // Idempotente. Se l'utente ha gia' una subscription attiva nel browser,
  // ci assicuriamo che sia salvata anche su DB.
  if (!isPushSupported()) return;
  if (Notification.permission !== "granted") return;
  const sub = await getCurrentSubscription();
  if (sub) await persistSubscription(sub);
}

export function configureSwNavigationBridge(navigateFn: (url: string) => void) {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
  navigator.serviceWorker.addEventListener("message", (event) => {
    const data = event.data;
    if (!data || typeof data !== "object") return;
    if (data.type === "navigate" && typeof data.url === "string") {
      navigateFn(data.url);
    }
    if (data.type === "push-subscription-changed") {
      refreshSubscriptionIfNeeded();
    }
  });
}
